import { FormEvent, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import { useGuicheDeleteStyles } from "./styles";
import Delete from "@material-ui/icons/Delete";
import { UserInScheduleWithPermissions } from "../../../../../../../../../interfaces/user";
import { IconButton, FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useRemoveUserProfile } from "../../../../../../../../../services/api-tansquery/user/profile-management";

type Props = {
    user: UserInScheduleWithPermissions;
    profileToRemove: string;
    canRemove?: boolean;
    orgaos?: Array<{ id: number; nome: string; sigla: string; can_remove: boolean }>;
    hasMultipleOrgaos?: boolean;
    buttonTrigger?: React.ReactNode;
}

export default function RemoveProfileButton({ 
    user, 
    profileToRemove, 
    canRemove = true,
    orgaos = [],
    hasMultipleOrgaos = false,
    buttonTrigger 
}: Props) {
    const [open, setOpen] = useState(false);
    const [selectedOrgaoId, setSelectedOrgaoId] = useState<number | undefined>(undefined);
    const [orgaosToSelect, setOrgaosToSelect] = useState<Array<{ id: number; nome: string; sigla: string }>>([]);
    const { classes } = useGuicheDeleteStyles();
    const queryClient = useQueryClient();
    const removeProfileHook = useRemoveUserProfile(user.id);

    const mutation = useMutation({
        mutationKey: ["remove-profile", user.id, profileToRemove, selectedOrgaoId],
        mutationFn: async () => {
            const result = await removeProfileHook.mutate(profileToRemove, selectedOrgaoId);
            return result;
        },
        onSuccess: async () => {
            toast.success("Perfil removido com sucesso.");
            await queryClient.invalidateQueries({ queryKey: ["user-profiles", user.id] });
            await queryClient.invalidateQueries({ queryKey: ["profile", "details", String(user.id)] });
            await queryClient.refetchQueries({ queryKey: ["profile", "details", String(user.id)] });
            setOpen(false);
            setSelectedOrgaoId(undefined);
            setOrgaosToSelect([]);
        },
        onError: (error: AxiosError) => {
            const errorData = (error?.response?.data as any);
            const errorMessage = errorData?.error || "Algo deu errado, tente novamente!";
            
            // Se o erro indica que precisa selecionar um órgão
            if (errorData?.requires_orgao_selection && errorData?.orgaos) {
                setOrgaosToSelect(errorData.orgaos);
                // Não fechar o dialog, apenas mostrar o select
            } else {
                toast.error(errorMessage);
            }
            console.log(error);
        },
    });

    async function handleSubmit(event: FormEvent<HTMLDivElement>) {
        event.preventDefault();

        if (mutation.isLoading) {
            return;
        }

        // Se há múltiplos órgãos e nenhum foi selecionado, mostrar seleção
        if (hasMultipleOrgaos && !selectedOrgaoId && orgaosToSelect.length === 0) {
            // Filtrar apenas órgãos que podem ser removidos
            const removableOrgaos = orgaos.filter(o => o.can_remove);
            if (removableOrgaos.length > 1) {
                setOrgaosToSelect(removableOrgaos.map(o => ({ id: o.id, nome: o.nome, sigla: o.sigla })));
                toast.warning("Selecione o órgão para remover o perfil.");
                return;
            } else if (removableOrgaos.length === 1) {
                setSelectedOrgaoId(removableOrgaos[0].id);
            }
        }

        await mutation.mutateAsync();
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    const getProfileDisplayName = (profile: string) => {
        const displayNames: Record<string, string> = {
            "Administrador de Atendimento": "Administrador",
            "Gerente de Atendimento": "Gerente de Atendimento",
            "Coordenador de Atendimento": "Coordenador de Atendimento",
            "Atendente": "Atendente"
        };
        return displayNames[profile] || profile;
    };

    // Se não pode remover, não renderizar o botão
    if (!canRemove) {
        return null;
    }

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <IconButton onClick={() => setOpen(true)}>
                    <Delete fontSize="small" style={{ color: "#706E7A" }} />
                </IconButton>
            )}
            <Dialog open={open} maxWidth="sm">
                <Card component="form" onSubmit={handleSubmit}>
                    <CardHeader
                        avatar={<Delete />}
                        className={classes.cardHeader}
                        title={
                            <Typography variant="h5">
                                Remover Perfil
                            </Typography>
                        }
                    />
                    <CardContent className={classes.cardContent}>
                        <Typography className={classes.textWarning}>
                            Você está prestes a remover o perfil <strong>{getProfileDisplayName(profileToRemove)}</strong> deste usuário.
                        </Typography>
                        {(hasMultipleOrgaos || orgaosToSelect.length > 0) && (
                            <FormControl fullWidth style={{ marginTop: 16, marginBottom: 16 }}>
                                <InputLabel id="orgao-select-label">Selecione o órgão</InputLabel>
                                <Select
                                    labelId="orgao-select-label"
                                    value={selectedOrgaoId || ""}
                                    onChange={(e) => setSelectedOrgaoId(e.target.value as number)}
                                    disabled={mutation.isLoading}
                                >
                                    {(orgaosToSelect.length > 0 ? orgaosToSelect : orgaos.filter(o => o.can_remove)).map((orgao) => (
                                        <MenuItem key={orgao.id} value={orgao.id}>
                                            {orgao.nome} ({orgao.sigla})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {mutation.isError && (mutation.error as any)?.response?.data?.requires_orgao_selection && (
                            <Typography className={classes.textWarning} color="error" style={{ marginTop: 16 }}>
                                {(mutation.error as any)?.response?.data?.error || "Selecione o órgão para remover o perfil."}
                            </Typography>
                        )}
                        <Typography className={classes.textWarning} style={{ marginTop: 16 }}>
                            Tem certeza que deseja continuar?
                        </Typography>
                        {mutation.isError && !(mutation.error as any)?.response?.data?.requires_orgao_selection && (
                            <Typography className={classes.textWarning} color="error">
                                {(mutation.error as any)?.response?.data?.error || "Erro ao remover perfil."}
                            </Typography>
                        )}
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={classes.duplicateButton}
                            disabled={mutation.isLoading || ((hasMultipleOrgaos || orgaosToSelect.length > 0) && !selectedOrgaoId)}
                        >
                            {mutation.isLoading ? "Removendo Perfil..." : "Remover Perfil"}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
